import {
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfYear,
  endOfYear,
  subWeeks,
  subYears,
  subDays,
} from "date-fns";

export type DateRangeOptionId =
  | "today"
  | "yesterday"
  | "thisWeek"
  | "lastWeek"
  | "thisMonth"
  | "lastMonth"
  | "last2Months"
  | "last3Months"
  | "thisYear"
  | "lastYear"
  | "allTime"
  | "custom";

export const DATE_RANGE_OPTION_KEYS: { key: string; value: DateRangeOptionId }[] = [
  { key: "common:dateRange.today", value: "today" },
  { key: "common:dateRange.yesterday", value: "yesterday" },
  { key: "common:dateRange.thisWeek", value: "thisWeek" },
  { key: "common:dateRange.lastWeek", value: "lastWeek" },
  { key: "common:dateRange.thisMonth", value: "thisMonth" },
  { key: "common:dateRange.lastMonth", value: "lastMonth" },
  { key: "common:dateRange.last2Months", value: "last2Months" },
  { key: "common:dateRange.last3Months", value: "last3Months" },
  { key: "common:dateRange.thisYear", value: "thisYear" },
  { key: "common:dateRange.lastYear", value: "lastYear" },
  { key: "common:dateRange.allTime", value: "allTime" },
  { key: "common:dateRange.custom", value: "custom" },
];

const VALID_OPTION_IDS = new Set<DateRangeOptionId>(DATE_RANGE_OPTION_KEYS.map((opt) => opt.value));

export const normalizeDateRangeOption = (option?: string): DateRangeOptionId | undefined => {
  if (!option) return undefined;
  if (VALID_OPTION_IDS.has(option as DateRangeOptionId)) return option as DateRangeOptionId;
  return undefined;
};

/** Returns preset bounds, or null when the option has no fixed range (allTime, custom). */
export const computePresetDateRange = (
  option: DateRangeOptionId,
  now: Date = new Date(),
): { from: Date; to: Date } | null => {
  switch (option) {
    case "allTime":
    case "custom":
      return null;
    case "today":
      return { from: startOfDay(now), to: endOfDay(now) };
    case "yesterday": {
      const yesterday = subDays(now, 1);
      return { from: startOfDay(yesterday), to: endOfDay(yesterday) };
    }
    case "thisWeek":
      return {
        from: startOfWeek(now, { weekStartsOn: 1 }),
        to: endOfWeek(now, { weekStartsOn: 1 }),
      };
    case "lastWeek": {
      const previousWeek = subWeeks(now, 1);
      return {
        from: startOfWeek(previousWeek, { weekStartsOn: 1 }),
        to: endOfWeek(previousWeek, { weekStartsOn: 1 }),
      };
    }
    case "thisMonth":
      return { from: startOfMonth(now), to: endOfMonth(now) };
    case "lastMonth": {
      const previousMonth = subMonths(now, 1);
      return { from: startOfMonth(previousMonth), to: endOfMonth(previousMonth) };
    }
    case "last2Months":
      return {
        from: startOfMonth(subMonths(now, 2)),
        to: endOfMonth(subMonths(now, 1)),
      };
    case "last3Months":
      return {
        from: startOfMonth(subMonths(now, 3)),
        to: endOfMonth(subMonths(now, 1)),
      };
    case "thisYear":
      return { from: startOfYear(now), to: endOfYear(now) };
    case "lastYear": {
      const previousYear = subYears(now, 1);
      return { from: startOfYear(previousYear), to: endOfYear(previousYear) };
    }
    default:
      return null;
  }
};

/**
 * Clamp a preset to the readable scope. A preset entirely before the scope has
 * no overlap, so collapse it to the first in-scope instant rather than
 * submitting an inverted From/To range or widening the requested period.
 */
export const clampPresetRangeToScope = (
  range: { from: Date; to: Date } | null,
  scopeMinDate: Date | null,
): { from: Date | null; to: Date | null } => {
  if (!range) return { from: null, to: null };
  const from = scopeMinDate && range.from < scopeMinDate ? scopeMinDate : range.from;
  return from > range.to ? { from, to: from } : { from, to: range.to };
};
